import { useState, useEffect } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];

const EMPTY_FORM = {
  name: '',
  passportNumber: '',
  dateOfBirth: '',
  gender: '',
  allergies: '',
  dietaryRequirements: '',
  safetyIssues: '',
  emergencyContactName: '',
  emergencyContactRelation: '',
  emergencyContactPhone: '',
};

function fmtDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <label className="block text-[10.5px] font-bold text-[#6B7280] uppercase tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder = '', type = 'text' }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
    />
  );
}

function Textarea({ value, onChange, placeholder = '', rows = 2 }) {
  return (
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white resize-none"
    />
  );
}

// ── Staff form panel ──────────────────────────────────────────────────────────

function StaffFormPanel({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  function set(field, val) { setForm((p) => ({ ...p, [field]: val })); }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
      <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
        <h3 className="text-[14.5px] font-bold text-[#111827]">{initial ? 'Edit Ground Staff' : 'Add Ground Staff'}</h3>
        <div className="flex gap-2">
          <button onClick={onCancel} disabled={saving}
            className="px-3 py-1.5 text-[12px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:bg-[#F9FAFB] transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-3 py-1.5 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {error && <p className="px-[18px] py-2 text-[12px] text-dr bg-[#FEF2F2]">{error}</p>}
      <div className="px-[18px] py-[16px] space-y-5">

        {/* Personal details */}
        <div>
          <p className="text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-3">Personal Details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldGroup label="Full Name *">
              <Input value={form.name} onChange={(v) => set('name', v)} placeholder="e.g. John Smith" />
            </FieldGroup>
            <FieldGroup label="Passport Number">
              <Input value={form.passportNumber} onChange={(v) => set('passportNumber', v)} placeholder="e.g. 123456789" />
            </FieldGroup>
            <FieldGroup label="Date of Birth">
              <Input type="date" value={form.dateOfBirth} onChange={(v) => set('dateOfBirth', v)} />
            </FieldGroup>
            <FieldGroup label="Gender">
              <select value={form.gender} onChange={(e) => set('gender', e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                <option value="">—</option>
                {GENDERS.map((g) => <option key={g}>{g}</option>)}
              </select>
            </FieldGroup>
          </div>
        </div>

        {/* Health & safety */}
        <div>
          <p className="text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-3">Health & Safety</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FieldGroup label="Allergies">
              <Textarea value={form.allergies} onChange={(v) => set('allergies', v)} placeholder="Any known allergies…" />
            </FieldGroup>
            <FieldGroup label="Dietary Requirements">
              <Textarea value={form.dietaryRequirements} onChange={(v) => set('dietaryRequirements', v)} placeholder="e.g. Vegetarian, Halal…" />
            </FieldGroup>
            <FieldGroup label="Safety Issues / Medical Notes">
              <Textarea value={form.safetyIssues} onChange={(v) => set('safetyIssues', v)} placeholder="Any medical or safety notes…" />
            </FieldGroup>
          </div>
        </div>

        {/* Emergency contact */}
        <div>
          <p className="text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-3">Emergency Contact</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FieldGroup label="Contact Name">
              <Input value={form.emergencyContactName} onChange={(v) => set('emergencyContactName', v)} placeholder="e.g. Jane Smith" />
            </FieldGroup>
            <FieldGroup label="Relation">
              <Input value={form.emergencyContactRelation} onChange={(v) => set('emergencyContactRelation', v)} placeholder="e.g. Spouse, Parent…" />
            </FieldGroup>
            <FieldGroup label="Phone Number">
              <Input type="tel" value={form.emergencyContactPhone} onChange={(v) => set('emergencyContactPhone', v)} placeholder="e.g. +44 7700 900000" />
            </FieldGroup>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function GroundStaff() {
  const [staffList,  setStaffList]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [search,     setSearch]     = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'groundStaff'), orderBy('name')),
      (snap) => {
        setStaffList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  async function handleAdd(form) {
    await addDoc(collection(db, 'groundStaff'), {
      ...form,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    setShowForm(false);
  }

  async function handleEdit(id, form) {
    await updateDoc(doc(db, 'groundStaff', id), {
      ...form,
      updatedAt: serverTimestamp(),
    });
    setEditingId(null);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this ground staff member? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'groundStaff', id));
    } catch (err) {
      console.error(err);
      alert('Failed to delete. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = staffList.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.gender || '').toLowerCase().includes(q)
    );
  });

  const editingStaff = editingId ? staffList.find((s) => s.id === editingId) : null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Ground Staff</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            Permanent staff profiles used for room allocation and tour operations. Saved until deleted.
          </p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-pb hover:bg-navy text-white text-[13px] font-semibold rounded-[10px] transition flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
          Add Staff
        </button>
      </div>

      {/* Add form */}
      {showForm && !editingId && (
        <StaffFormPanel
          initial={null}
          onSave={handleAdd}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Edit form */}
      {editingId && editingStaff && (
        <StaffFormPanel
          initial={editingStaff}
          onSave={(form) => handleEdit(editingId, form)}
          onCancel={() => setEditingId(null)}
        />
      )}

      {/* Search */}
      {staffList.length > 0 && (
        <div className="relative max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
          <input
            type="text"
            placeholder="Search staff…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
          />
        </div>
      )}

      {/* Staff list */}
      {loading ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
          {staffList.length === 0 ? (
            <>
              <p className="text-[14px] font-bold text-[#111827] mb-2">No ground staff yet</p>
              <p className="text-[13px] text-[#6B7280] mb-4">Add staff members to assign them to rooms on tours.</p>
              <button onClick={() => setShowForm(true)} className="text-[13px] font-semibold text-pb hover:text-navy transition">
                + Add your first staff member
              </button>
            </>
          ) : (
            <p className="text-[13px] text-[#9CA3AF]">No staff match your search.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
          <div className="px-[18px] py-[12px] border-b border-[#F1F3F6] bg-surface">
            <p className="text-[12px] font-semibold text-[#6B7280]">{filtered.length} staff member{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="divide-y divide-[#F1F3F6]">
            {filtered.map((s) => (
              <div key={s.id} className="px-[18px] py-[14px] hover:bg-surface transition-colors">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-pb/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[14px] font-bold text-pb">{(s.name || '?')[0].toUpperCase()}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[13.5px] font-bold text-[#111827]">{s.name}</p>
                      {s.gender && (
                        <span className="text-[11px] font-semibold text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">{s.gender}</span>
                      )}
                      {s.dateOfBirth && (
                        <span className="text-[11px] text-[#9CA3AF]">DOB: {fmtDate(s.dateOfBirth)}</span>
                      )}
                    </div>

                    <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                      {s.passportNumber && (
                        <p className="text-[11.5px] text-[#6B7280]">
                          <span className="text-[#9CA3AF]">Passport: </span>{s.passportNumber}
                        </p>
                      )}
                      {s.emergencyContactName && (
                        <p className="text-[11.5px] text-[#6B7280]">
                          <span className="text-[#9CA3AF]">Emergency: </span>{s.emergencyContactName}
                          {s.emergencyContactRelation ? ` (${s.emergencyContactRelation})` : ''}
                          {s.emergencyContactPhone ? ` · ${s.emergencyContactPhone}` : ''}
                        </p>
                      )}
                      {s.allergies && (
                        <p className="text-[11.5px] text-dr">
                          ⚠️ Allergies: {s.allergies}
                        </p>
                      )}
                      {s.dietaryRequirements && (
                        <p className="text-[11.5px] text-[#6B7280]">
                          <span className="text-[#9CA3AF]">Diet: </span>{s.dietaryRequirements}
                        </p>
                      )}
                      {s.safetyIssues && (
                        <p className="text-[11.5px] text-[#92400E]">
                          ⚠️ {s.safetyIssues}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => { setEditingId(s.id); setShowForm(false); }}
                      className="px-3 py-1.5 text-[11.5px] font-semibold text-pb border border-pb/30 rounded-[7px] hover:bg-pb/5 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(s.id)}
                      disabled={deletingId === s.id}
                      className="px-3 py-1.5 text-[11.5px] font-semibold text-dr border border-dr/20 rounded-[7px] hover:bg-red-50 transition disabled:opacity-40"
                    >
                      {deletingId === s.id ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
